/**
 * Parse an AI SDK v6 SSE stream (UIMessageChunk format).
 *
 * AI SDK v6 sends JSON-encoded chunks as SSE `data:` lines:
 *   data: {"type":"text-delta","delta":"hello","id":"msg-1"}
 *   data: {"type":"data-entity_proposal","data":{...}}
 *   data: {"type":"error","errorText":"..."}
 *   data: [DONE]
 *
 * This replaces `readSSEStream()` for migrated consumers while keeping
 * the same callback pattern. `readSSEStream()` is retained for the
 * walkthrough progress stream (not migrated in this change).
 */

/** Chunk types we handle on the client. */
export interface TextDeltaChunk {
  type: "text-delta";
  id: string;
  delta: string;
}

export interface TextStartChunk {
  type: "text-start";
  id: string;
}

export interface TextEndChunk {
  type: "text-end";
  id: string;
}

export interface ReasoningDeltaChunk {
  type: "reasoning-delta";
  id: string;
  delta: string;
}

export interface DataChunk<T = unknown> {
  type: `data-${string}`;
  id?: string;
  data: T;
  transient?: boolean;
}

export interface ErrorChunk {
  type: "error";
  errorText: string;
}

export interface FinishChunk {
  type: "finish";
}

export type AISDKChunk =
  | TextDeltaChunk
  | TextStartChunk
  | TextEndChunk
  | ReasoningDeltaChunk
  | DataChunk
  | ErrorChunk
  | FinishChunk
  | { type: string; [key: string]: unknown };

/**
 * Type guard for custom data-* chunks.
 */
export function isDataChunk(chunk: AISDKChunk): chunk is DataChunk {
  return typeof chunk.type === "string" && chunk.type.startsWith("data-");
}

/**
 * Type guard for a specific data chunk type.
 */
export function isDataChunkOf<T>(
  chunk: AISDKChunk,
  name: string,
): chunk is DataChunk<T> {
  return chunk.type === `data-${name}`;
}

/**
 * Read an AI SDK v6 SSE stream, parse JSON chunks, and call onChunk for each.
 *
 * Return `false` from onChunk to stop reading early (e.g., on error).
 * Always releases the reader lock when done.
 */
export async function readAISDKStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onChunk: (chunk: AISDKChunk) => boolean | undefined,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(":")) continue; // SSE comment/ping
        if (!trimmed.startsWith("data:")) continue;

        const jsonStr = trimmed.slice(5).trim();
        if (!jsonStr || jsonStr === "[DONE]") continue;

        let parsed: AISDKChunk;
        try {
          parsed = JSON.parse(jsonStr) as AISDKChunk;
        } catch {
          continue; // Skip malformed JSON
        }

        if (onChunk(parsed) === false) return;
      }
    }

    // Process remaining buffer
    if (buffer.trim().startsWith("data:")) {
      const jsonStr = buffer.trim().slice(5).trim();
      if (jsonStr && jsonStr !== "[DONE]") {
        try {
          const parsed = JSON.parse(jsonStr) as AISDKChunk;
          onChunk(parsed);
        } catch {
          // Skip malformed trailing data
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
